/**
 * Single source of truth for status colors (KPI tiles, pills, session cards).
 * Schedule page, Scheduler summary, participant pills, and booked-session rows all derive from this.
 */

export type StatusVisualColorKey =
  | 'blue'
  | 'purple'
  | 'green'
  | 'gray'
  | 'yellow'
  | 'red'
  | 'orange'
  | 'rose'
  | 'stone'
  | 'cyan';

const norm = (status: string) =>
  String(status || '')
    .toLowerCase()
    .trim()
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Pastel tile background (Schedule + Scheduler StatusCard). */
export const KPI_TILE_BG: Record<StatusVisualColorKey, string> = {
  blue: 'bg-blue-50',
  purple: 'bg-purple-50',
  green: 'bg-green-50',
  gray: 'bg-gray-50',
  yellow: 'bg-yellow-50',
  red: 'bg-red-50',
  orange: 'bg-orange-50',
  rose: 'bg-rose-50',
  stone: 'bg-stone-50',
  cyan: 'bg-cyan-50',
};

/** Large KPI number color (matches tile hue). */
export const KPI_NUMBER_CLASS: Record<StatusVisualColorKey, string> = {
  blue: 'text-blue-600',
  purple: 'text-purple-600',
  green: 'text-green-600',
  gray: 'text-gray-700',
  yellow: 'text-amber-600',
  red: 'text-red-600',
  orange: 'text-orange-600',
  rose: 'text-rose-600',
  stone: 'text-stone-700',
  cyan: 'text-cyan-600',
};

/** Solid status dot (legend tooltips / metric popovers). */
export const STATUS_DOT_CLASS: Record<StatusVisualColorKey, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  green: 'bg-green-500',
  gray: 'bg-gray-400',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  rose: 'bg-rose-500',
  stone: 'bg-stone-800',
  cyan: 'bg-cyan-500',
};

/** Compact pill: surface + text + border (participant / table badges). */
export const STATUS_PILL_CLASS: Record<StatusVisualColorKey, string> = {
  blue: 'bg-blue-50 text-blue-600 border-blue-200',
  purple: 'bg-purple-50 text-purple-600 border-purple-200',
  green: 'bg-green-50 text-green-600 border-green-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200',
  yellow: 'bg-yellow-50 text-amber-600 border-amber-200',
  red: 'bg-red-50 text-red-600 border-red-200',
  orange: 'bg-orange-50 text-orange-600 border-orange-200',
  rose: 'bg-rose-50 text-rose-600 border-rose-200',
  stone: 'bg-stone-50 text-stone-700 border-stone-200',
  cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
};

/** Booked session row / card surfaces (slightly stronger hover). */
export const SESSION_CARD_STYLE: Record<
  StatusVisualColorKey,
  { bg: string; hover: string; border: string; text: string }
> = {
  blue: {
    bg: 'bg-blue-50',
    hover: 'hover:bg-blue-100',
    border: 'border-blue-200',
    text: 'text-blue-900',
  },
  purple: {
    bg: 'bg-purple-50',
    hover: 'hover:bg-purple-100',
    border: 'border-purple-200',
    text: 'text-purple-900',
  },
  green: {
    bg: 'bg-green-50',
    hover: 'hover:bg-green-100',
    border: 'border-green-200',
    text: 'text-green-900',
  },
  gray: {
    bg: 'bg-gray-50',
    hover: 'hover:bg-gray-100',
    border: 'border-gray-200',
    text: 'text-gray-900',
  },
  yellow: {
    bg: 'bg-amber-50',
    hover: 'hover:bg-amber-100',
    border: 'border-amber-200',
    text: 'text-amber-900',
  },
  red: {
    bg: 'bg-red-50',
    hover: 'hover:bg-red-100',
    border: 'border-red-200',
    text: 'text-red-900',
  },
  orange: {
    bg: 'bg-orange-50',
    hover: 'hover:bg-orange-100',
    border: 'border-orange-200',
    text: 'text-orange-900',
  },
  rose: {
    bg: 'bg-rose-50',
    hover: 'hover:bg-rose-100',
    border: 'border-rose-200',
    text: 'text-rose-900',
  },
  stone: {
    bg: 'bg-stone-50',
    hover: 'hover:bg-stone-100',
    border: 'border-stone-200',
    text: 'text-stone-900',
  },
  cyan: {
    bg: 'bg-cyan-50',
    hover: 'hover:bg-cyan-100',
    border: 'border-cyan-200',
    text: 'text-cyan-900',
  },
};

/** Schedule page KPI buckets → same keys as SESSION_STATUS_CARD_CONFIG where they overlap. */
export const SCHEDULE_STAT_BUCKET_VISUAL: Record<
  | 'notStarted'
  | 'inProgress'
  | 'completed'
  | 'partiallyCompleted'
  | 'cancelled'
  | 'calledOff'
  | 'noShow',
  StatusVisualColorKey
> = {
  notStarted: 'blue',
  inProgress: 'purple',
  completed: 'green',
  partiallyCompleted: 'cyan',
  cancelled: 'gray',
  calledOff: 'orange',
  noShow: 'rose',
};

/**
 * Participant workflow + session-status labels on people (Scheduler list, Participants table).
 * Differs from `resolveSessionStatusVisualKey` for reschedule vs rescheduled.
 */
const PARTICIPANT_STATUS_VISUAL: Record<string, StatusVisualColorKey> = {
  disqualified: 'red',
  blocked: 'stone',
  overbooked: 'yellow',
  'max overbooks reached': 'yellow',
  cancelled: 'gray',
  canceled: 'gray',
  'no show': 'rose',
  'in progress': 'purple',
  endorsed: 'purple',
  contacting: 'yellow',
  scheduling: 'yellow',
  scheduled: 'blue',
  completed: 'green',
  'partially completed': 'cyan',
  unreachable: 'gray',
  available: 'purple',
  dropout: 'red',
  reschedule: 'yellow',
  rescheduled: 'blue',
  'called off': 'orange',
  calledoff: 'orange',
};

export function resolveParticipantStatusVisualKey(
  status: string | null | undefined,
): StatusVisualColorKey {
  if (!status) return 'gray';
  return PARTICIPANT_STATUS_VISUAL[norm(status)] ?? 'gray';
}

/**
 * Booked session row status (Schedule / Booked Sessions). Bundles reschedule + active + scheduled as blue.
 */
export function resolveSessionStatusVisualKey(
  status: string | null | undefined,
): StatusVisualColorKey {
  if (!status) return 'blue';
  const s = norm(status);
  if (s === 'no show' || s === 'noshow') return 'rose';
  if (s === 'in progress' || s === 'inprogress') return 'purple';
  if (s === 'completed') return 'green';
  if (s === 'cancelled' || s === 'canceled') return 'gray';
  if (s === 'called off' || s === 'calledoff') return 'orange';
  if (s === 'partially completed') return 'cyan';
  if (s === 'overbooked' || s === 'max overbooks reached') return 'yellow';
  if (s === 'disqualified') return 'red';
  if (s === 'blocked') return 'stone';
  if (
    s === 'scheduled' ||
    s === 'active' ||
    s === 'not started' ||
    s === 'rescheduled' ||
    s === 'reschedule'
  ) {
    return 'blue';
  }
  return 'blue';
}

export function getParticipantSchedulePillClass(status: string): string {
  const key = resolveParticipantStatusVisualKey(status);
  return STATUS_PILL_CLASS[key];
}

export function getSessionStatusCardStyle(status: string | null | undefined) {
  const key = resolveSessionStatusVisualKey(status);
  return SESSION_CARD_STYLE[key];
}

/** Pill for a booked-session status (Schedule / session notes; reschedule → blue with session rules). */
export function getBookedSessionStatusPillClass(status: string): string {
  return STATUS_PILL_CLASS[resolveSessionStatusVisualKey(status)];
}

export function getKpiClasses(visual: StatusVisualColorKey): {
  tileBg: string;
  numberClass: string;
} {
  return { tileBg: KPI_TILE_BG[visual], numberClass: KPI_NUMBER_CLASS[visual] };
}
